import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileDown, Check, Loader2, Lock, Mail, User, Building2, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getVisitorId = () => {
  let vid = localStorage.getItem('ab_visitor_id');
  if (!vid) {
    vid = crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('ab_visitor_id', vid);
  }
  return vid;
};

const trackABEvent = (experimentId, variant, event, blogSlug, visitorId) => {
  if (!experimentId) return;
  fetch(`${BACKEND_URL}/api/ab-tests/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ experiment_id: experimentId, variant, event, blog_slug: blogSlug, visitor_id: visitorId }),
  }).catch(() => {});
};

const LeadMagnetCTA = ({ blogSlug }) => {
  const [magnet, setMagnet] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', consent: true });

  // A/B test state
  const [abExperimentId, setAbExperimentId] = useState(null);
  const [abVariant, setAbVariant] = useState(null);
  const [abData, setAbData] = useState(null);
  const visitorId = useRef(getVisitorId());
  const impressionTracked = useRef(false);

  useEffect(() => {
    if (!blogSlug) return;

    const fetchMagnetAndAB = async () => {
      // Fetch A/B assignment first
      try {
        const abRes = await fetch(`${BACKEND_URL}/api/ab-tests/assign/${blogSlug}?visitor_id=${visitorId.current}`);
        if (abRes.ok) {
          const ab = await abRes.json();
          if (ab.experiment_id && ab.variant && ab.data) {
            setAbExperimentId(ab.experiment_id);
            setAbVariant(ab.variant);
            setAbData(ab.data);
            // Use the AB variant's magnet slug to fetch magnet
            if (ab.data.lead_magnet_slug) {
              const magnetRes = await fetch(`${BACKEND_URL}/api/lead-magnets/by-blog/${blogSlug}`);
              if (magnetRes.ok) {
                const magnetData = await magnetRes.json();
                if (magnetData.magnet) setMagnet(magnetData.magnet);
              }
            }
            return;
          }
        }
      } catch (err) { console.error('A/B test fetch failed:', err); }

      // Fallback: no A/B test, fetch magnet normally
      try {
        const res = await fetch(`${BACKEND_URL}/api/lead-magnets/by-blog/${blogSlug}`);
        if (res.ok) {
          const data = await res.json();
          if (data.magnet) setMagnet(data.magnet);
        }
      } catch (err) { console.error('Lead magnet fetch failed:', err); }
    };

    fetchMagnetAndAB();
  }, [blogSlug]);

  // Track impression once visible
  useEffect(() => {
    if ((magnet || abData) && !impressionTracked.current) {
      impressionTracked.current = true;
      trackABEvent(abExperimentId, abVariant, 'impression', blogSlug, visitorId.current);
    }
  }, [magnet, abData, abExperimentId, abVariant, blogSlug]);

  if (!magnet && !abData) return null;

  // Resolve display values: A/B variant overrides magnet defaults
  const headline = abData?.cta_headline || magnet?.cta_headline || magnet?.title || '';
  const description = abData?.cta_description || magnet?.cta_description || magnet?.subtitle || '';
  const buttonText = abData?.button_text || 'Download Free PDF';
  const thankYouMsg = abData?.thank_you_message || magnet?.thank_you_message || 'Thank you!';
  const isGated = abData ? abData.email_gate_enabled : (magnet?.email_gate_enabled ?? true);
  const magnetSlug = abData?.lead_magnet_slug || magnet?.slug;
  const magnetType = abData?.lead_magnet_type || magnet?.type;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setDownloading(true);
    trackABEvent(abExperimentId, abVariant, 'form_submit', blogSlug, visitorId.current);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-magnets/${magnetSlug}/download`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        setDownloadUrl(`${BACKEND_URL}${data.download_url}`);
        setSubmitted(true);
        trackABEvent(abExperimentId, abVariant, 'download', blogSlug, visitorId.current);
      }
    } catch (err) { console.error('Download failed:', err); }
    setDownloading(false);
  };

  const handleDirectDownload = () => {
    trackABEvent(abExperimentId, abVariant, 'click', blogSlug, visitorId.current);
    trackABEvent(abExperimentId, abVariant, 'download', blogSlug, visitorId.current);
    window.open(`${BACKEND_URL}/api/lead-magnets/${magnetSlug}/file`, '_blank');
  };

  const handleUnlockClick = () => {
    trackABEvent(abExperimentId, abVariant, 'click', blogSlug, visitorId.current);
    setShowForm(true);
    trackABEvent(abExperimentId, abVariant, 'form_start', blogSlug, visitorId.current);
  };

  if (submitted) {
    return (
      <div className="my-8 p-6 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-center" data-testid="lead-magnet-thankyou">
        <Check className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-emerald-800 mb-2">{thankYouMsg}</h3>
        <p className="text-sm text-emerald-700 mb-4">Your resource is ready to download.</p>
        <Button onClick={() => window.open(downloadUrl, '_blank')}
          className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-pdf-btn">
          <FileDown className="w-4 h-4 mr-2" /> Download PDF
        </Button>
      </div>
    );
  }

  return (
    <div className="my-8 border-2 border-emerald-200 rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="lead-magnet-cta">
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileDown className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded">
                Free {magnetType?.replace(/_/g, ' ')}
              </span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-testid="cta-title">
              {headline}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {description}
            </p>

            {!isGated ? (
              <Button onClick={handleDirectDownload} className="bg-emerald-600 hover:bg-emerald-700" data-testid="direct-download-btn">
                <FileDown className="w-4 h-4 mr-2" /> {buttonText}
              </Button>
            ) : !showForm ? (
              <Button onClick={handleUnlockClick} className="bg-emerald-600 hover:bg-emerald-700" data-testid="unlock-download-btn">
                <Lock className="w-4 h-4 mr-2" /> {buttonText}
              </Button>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="lead-magnet-form">
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Your name *" className="pl-9 h-9 text-sm" value={form.name} required
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="lm-name" />
                  </div>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Email *" type="email" className="pl-9 h-9 text-sm" value={form.email} required
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="lm-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Company" className="pl-9 h-9 text-sm" value={form.company}
                      onChange={e => setForm(f => ({ ...f, company: e.target.value }))} data-testid="lm-company" />
                  </div>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input placeholder="Phone" className="pl-9 h-9 text-sm" value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="lm-phone" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-500">
                  <input type="checkbox" checked={form.consent} onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600" data-testid="lm-consent" />
                  I agree to receive communications from Virtual CFO Consultancy
                </label>
                <Button type="submit" disabled={downloading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-download-btn">
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  {downloading ? 'Processing...' : buttonText}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadMagnetCTA;
